/**
 * Utility functions for calculating section totals in scenario templates
 */

/**
 * Calculate the section total for a header/sub-header with section total configuration
 * @param {Array} rows - All rows in the template
 * @param {number} headerIndex - Index of the header/sub-header with section total
 * @returns {Object} Calculated totals and metadata
 */
export const calculateSectionTotal = (rows, headerIndex) => {
  const headerRow = rows[headerIndex];
  
  if (!headerRow || !headerRow.hasSectionTotal || !headerRow.sectionTotalConfig) {
    return null;
  }
  
  const config = headerRow.sectionTotalConfig;
  const { scoreTypes, includeSubSections, endAtNextHeader, customEndIndex } = config;
  
  // Determine the end index for calculation
  let endIndex = customEndIndex || rows.length - 1;
  
  if (endAtNextHeader && !customEndIndex) {
    // Find the next header of the same or higher level
    const headerType = headerRow.type;
    const isMainHeader = headerType === 'header';
    
    for (let i = headerIndex + 1; i < rows.length; i++) {
      const currentRow = rows[i];
      
      // Stop at next main header for both main headers and sub-headers
      if (currentRow.type === 'header') {
        endIndex = i - 1;
        break;
      }
      
      // For sub-headers, also stop at next sub-header if not including sub-sections
      if (!isMainHeader && !includeSubSections && currentRow.type === 'sub-header') {
        endIndex = i - 1;
        break;
      }
    }
  }
  
  // Calculate totals
  const totals = {
    score: 0,
    value: 0,
    scoreCount: 0,
    valueCount: 0,
    itemCount: 0,
    criticalCount: 0
  };
  
  const includedRows = [];
  
  for (let i = headerIndex + 1; i <= endIndex; i++) {
    const row = rows[i];
    
    if (!row) continue;
    
    // Skip headers, sub-headers, descriptions, and blank rows from totaling
    if (row.type === 'header' || row.type === 'sub-header' || row.type === 'description' || row.type === 'blank-row') {
      continue;
    }
    
    // Handle stacked rows specially
    if (row.moduleType === 'stacked-row' && row.stackedObjectives) {
      row.stackedObjectives.forEach(objective => {
        if (scoreTypes.includes('score') && objective.score) {
          const scoreVal = parseFloat(objective.score) || 0;
          totals.score += scoreVal;
          totals.scoreCount++;
        }
        
        if (scoreTypes.includes('value') && objective.value) {
          const valueVal = parseFloat(objective.value) || 0;
          totals.value += valueVal;
          totals.valueCount++;
        }
        
        if (objective.critical) {
          totals.criticalCount++;
        }
        
        totals.itemCount++;
      });
      
      includedRows.push(i);
    } else {
      // Regular row
      if (scoreTypes.includes('score') && row.score) {
        const scoreVal = parseFloat(row.score) || 0;
        totals.score += scoreVal;
        totals.scoreCount++;
      }
      
      if (scoreTypes.includes('value') && row.value) {
        const valueVal = parseFloat(row.value) || 0;
        totals.value += valueVal;
        totals.valueCount++;
      }
      
      if (row.critical) {
        totals.criticalCount++;
      }
      
      if (row.item && row.item.trim()) {
        totals.itemCount++;
      }
      
      includedRows.push(i);
    }
  }
  
  return {
    totals,
    includedRows,
    startIndex: headerIndex + 1,
    endIndex,
    config
  };
};

/**
 * Get all sections with totals in a template
 * @param {Array} rows - All rows in the template
 * @returns {Array} Array of section total calculations
 */
export const getAllSectionTotals = (rows) => {
  const sectionTotals = [];
  
  rows.forEach((row, index) => {
    if (row.hasSectionTotal) {
      const calculation = calculateSectionTotal(rows, index);
      if (calculation) {
        sectionTotals.push({
          headerIndex: index,
          headerText: row.text || 'Unnamed Section',
          headerType: row.type,
          ...calculation
        });
      }
    }
  });
  
  return sectionTotals;
};

/**
 * Find the best insertion point for a section total row
 * @param {Array} rows - All rows in the template
 * @param {number} headerIndex - Index of the header with section total
 * @returns {number} Index where the total row should be inserted
 */
export const findSectionTotalInsertionPoint = (rows, headerIndex) => {
  const calculation = calculateSectionTotal(rows, headerIndex);
  
  if (!calculation) return -1;
  
  // Insert after the last included row
  return calculation.endIndex + 1;
};

/**
 * Calculate totals for a section-total row based on included row indices
 * @param {Array} rows - All rows in the template
 * @param {Array} includedRowIndices - Array of row indices to include in calculation
 * @returns {Object} Calculated totals {score: number, value: number}
 */
export const calculateIncludedRowsTotals = (rows, includedRowIndices) => {
  if (!includedRowIndices || includedRowIndices.length === 0) {
    return { score: 0, value: 0 };
  }
  
  let totals = { score: 0, value: 0 };
  
  includedRowIndices.forEach(rowIndex => {
    const row = rows[rowIndex];
    if (!row) return;
    
    // Skip if this is a header, sub-header, description, blank row, or section total
    if (['header', 'sub-header', 'description', 'blank-row', 'section-total'].includes(row.type)) {
      return;
    }
    
    // Handle stacked rows
    if (row.moduleType === 'stacked-row' && row.stackedObjectives) {
      row.stackedObjectives.forEach(objective => {
        const score = parseFloat(objective.score) || 0;
        const value = parseFloat(objective.value) || 0;
        totals.score += score;
        totals.value += value;
      });
    } else {
      // Regular row
      const score = parseFloat(row.score) || 0;
      const value = parseFloat(row.value) || 0;
      totals.score += score;
      totals.value += value;
    }
  });
  
  return totals;
};
